import type {
  AcademicRiskActionRepository,
  ActionType,
  ActionsResponse,
  CreateActionPayload,
  CreateActionResponse,
  DeleteActionResponse,
  EndOfActionResponse,
  RiskType,
  UpdateActionPayload,
  UpdateActionResponse,
} from '@/domains/academicRisk';
import { academicRiskActionApiService } from '../http/academicRiskAction.api';

/**
 * Implementación del repositorio de acciones académicas usando API service
 * Actúa como intermediario entre el dominio y la capa HTTP para operaciones CRUD
 * de acciones académicas, validando respuestas antes de devolverlas
 */
class AcademicRiskActionApiRepository implements AcademicRiskActionRepository {
  /**
   * Obtiene todas las acciones académicas de un estudiante específico
   * @param cod_incorporacion_estudiante - Código único de incorporación del estudiante
   * @throws Error si no se obtienen datos válidos del API
   */
  async getActions(
    cod_incorporacion_estudiante: string,
  ): Promise<ActionsResponse> {
    const getResponse = await academicRiskActionApiService.getActions(
      cod_incorporacion_estudiante,
    );

    if (!getResponse)
      throw new Error('Error al obtener las acciones sobre un estudiante');

    return getResponse;
  }

  /**
   * Obtiene el catálogo de tipos de acciones disponibles en el sistema
   * Filtra únicamente los tipos de acciones personales (personal: true)
   * @throws Error si no se obtienen datos válidos del API
   */
  async getActionTypes(): Promise<ActionType[]> {
    const actionTypes = await academicRiskActionApiService.getActionTypes();

    if (!actionTypes) throw new Error('Error al obtener tipos de acciones');

    return actionTypes;
  }

  /**
   * Obtiene el catálogo de tipos de riesgos académicos disponibles
   * @throws Error si no se obtienen datos válidos del API
   */
  async getRiskTypes(): Promise<RiskType[]> {
    const riskTypes = await academicRiskActionApiService.getRiskTypes();

    if (!riskTypes) throw new Error('Error al obtener los tipos de riesgo');

    return riskTypes;
  }

  /**
   * Registra una nueva acción académica para uno o varios estudiantes
   * @param payload - Datos de la acción a crear incluyendo estudiantes afectados
   * @throws Error si la operación de creación falla
   */
  async createAction(
    payload: CreateActionPayload,
  ): Promise<CreateActionResponse> {
    const createResponse =
      await academicRiskActionApiService.createAction(payload);

    if (!createResponse)
      throw new Error('Error al crear la acción sobre el estudiante');

    return createResponse;
  }

  /**
   * Actualiza las observaciones y acuerdos de una incorporación de acción específica
   * @param cod_incorporacion_accion - Código único de la incorporación de acción
   * @param payload - Datos actualizados (descripción y acuerdo)
   * @throws Error si la operación de actualización falla
   */
  async updateAction(
    cod_incorporacion_accion: number,
    payload: UpdateActionPayload,
  ): Promise<UpdateActionResponse> {
    const updateResponse = await academicRiskActionApiService.updateAction(
      cod_incorporacion_accion,
      payload,
    );

    if (!updateResponse)
      throw new Error('Error al actualizar la acción sobre el estudiante');

    return updateResponse;
  }

  async endOfAction(
    cod_incorporacion_accion: number,
  ): Promise<EndOfActionResponse> {
    const endResponse = await academicRiskActionApiService.endOfAction(
      cod_incorporacion_accion,
    );

    if (!endResponse) throw new Error('Error al cerrar una acción');

    return endResponse;
  }

  /**
   * Elimina una incorporación de acción académica del sistema
   * Realiza validación de respuesta antes de retornar
   * @param cod_incorporacion_accion - Código único de la incorporación de acción a eliminar
   * @throws Error si la operación de eliminación falla
   */
  async deleteAction(
    cod_incorporacion_accion: number,
  ): Promise<DeleteActionResponse> {
    const deleteResponse = await academicRiskActionApiService.deleteAction(
      cod_incorporacion_accion,
    );

    if (!deleteResponse)
      throw new Error('Error al eliminar la acción sobre un estudiante');

    return deleteResponse;
  }
}

export const academicRiskActionApiRepository =
  new AcademicRiskActionApiRepository();
